<script setup lang="ts">
import { AP_006_OPENING_SLIDES } from "~/constant";

const props = defineProps<{
  formattedDate: string;
}>();

const emit = defineEmits<{
  (e: "open"): void;
}>();

const { guestText, openInvitation } = useOpeningCard();
const { coupleName } = storeToRefs(useTemplateStore());

async function onOpen() {
  if (await openInvitation()) {
    emit("open");
  }
}
</script>

<template>
  <div
    class="ap-006-opening-card fixed inset-0 z-60 flex items-center justify-center overflow-hidden bg-primary-950"
    role="dialog"
    aria-modal="true"
    aria-labelledby="cover-dialog-title"
    aria-label="Kartu Undangan Pernikahan"
  >
    <!-- Opening Media -->
    <div class="opening-media absolute inset-0">
      <PImageFade
        :images="AP_006_OPENING_SLIDES"
      />
    </div>

    <!-- Background Overlay -->
    <div class="opening-overlay absolute inset-0" />

    <!-- Content -->
    <div class="opening-content relative z-1 flex w-full max-w-[min(100vw,500px)] flex-col justify-between px-7 text-center text-white sm:px-10 lg:max-w-[34rem]">
      <header class="opening-heading flex flex-col items-center gap-4">
        <p class="text-kicker">
          The Wedding Of
        </p>

        <h1
          id="cover-dialog-title"
          class="text-couple-name"
        >
          {{ coupleName }}
        </h1>

        <p
          v-if="props.formattedDate"
          class="font-serif font-semibold text-white/86 text-base"
        >
          {{ props.formattedDate }}
        </p>
      </header>

      <footer class="opening-footer mx-auto flex w-full max-w-sm flex-col items-center gap-4 lg:gap-3">
        <div class="flex flex-col gap-1">
          <p class="text-kicker font-serif text-white/78">
            Kepada
          </p>

          <h2 class="ap-006-glow font-sans text-xl font-semibold leading-tight text-white xs:text-2xl sm:text-3xl">
            {{ guestText }}
          </h2>
        </div>

        <p class="text-copy font-sans! max-w-sm">
          Dengan segala hormat, kami mengundang Anda untuk menghadiri acara pernikahan kami.
        </p>

        <UButton
          label="Buka Undangan"
          variant="glass"
          size="lg"
          class="mt-2 rounded-full!"
          @click="onOpen"
        />
      </footer>
    </div>
  </div>
</template>

<style scoped>
.ap-006-opening-card {
  height: 100vh;
  min-height: 100svh;
}

.opening-content {
  min-height: 100%;
  padding-top: calc(5rem + env(safe-area-inset-top));
  padding-bottom: calc(4.5rem + env(safe-area-inset-bottom));
}

.opening-media {
  transition:
    transform 900ms cubic-bezier(0.22, 0.61, 0.36, 1),
    filter 900ms cubic-bezier(0.22, 0.61, 0.36, 1);
}

.opening-overlay {
  background:
    radial-gradient(circle at 50% 44%, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0) 34%),
    linear-gradient(180deg, rgba(5, 5, 5, 0.38), rgba(5, 5, 5, 0.16) 34%, rgba(5, 5, 5, 0.94)),
    linear-gradient(90deg, rgba(5, 5, 5, 0.5), rgba(5, 5, 5, 0.1) 34%, rgba(5, 5, 5, 0.5));
}

@media (min-width: 1024px) {
  .opening-media :deep(.fade-image) {
    object-position: 50% 38%;
  }

  .opening-overlay {
    background:
      radial-gradient(circle at 50% 44%, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0) 30%),
      linear-gradient(180deg, rgba(5, 5, 5, 0.48), rgba(5, 5, 5, 0.12) 32%, rgba(5, 5, 5, 0.96)),
      linear-gradient(90deg, rgba(5, 5, 5, 0.72), rgba(5, 5, 5, 0.12) 28%, rgba(5, 5, 5, 0.72));
  }
}

:global(.invitation-cover-leave-to) .opening-media {
  filter: blur(6px);
  transform: scale(1.06);
}

@supports (height: 100dvh) {
  .ap-006-opening-card {
    height: 100dvh;
    min-height: 100dvh;
  }
}

@media (max-height: 740px) {
  .opening-content {
    padding-top: calc(3.25rem + env(safe-area-inset-top));
    padding-bottom: calc(2.25rem + env(safe-area-inset-bottom));
  }

  .opening-heading,
  .opening-footer {
    gap: 0.75rem;
  }
}

@media (max-height: 640px) {
  .opening-content {
    padding-top: calc(2.25rem + env(safe-area-inset-top));
    padding-bottom: calc(1.25rem + env(safe-area-inset-bottom));
  }

  .opening-footer {
    gap: 0.55rem;
  }

  .opening-footer :deep(.text-copy) {
    line-height: 1.42;
  }
}
</style>
